import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, FileText } from "lucide-react";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BUSINESS, IMAGES, API_URL } from "@/lib/site";

const CATEGORIES = [
  { key: "all", label: "All Products" },
  { key: "mobiles", label: "Mobiles & Accessories" },
  { key: "appliances", label: "Home Appliances" },
  { key: "kitchen", label: "Kitchen Appliances" },
  { key: "electronics", label: "General Electronics" },
];

const CATEGORY_IMG = {
  mobiles: IMAGES.mobile,
  appliances: IMAGES.appliances,
  kitchen: IMAGES.kitchen,
  electronics: IMAGES.electronics,
};

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    document.title = "Products & Prices | SUDHEER ELECTRONIC";
    axios.get(`${API_URL}/products`)
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const shown = filter === "all" ? products : products.filter((p) => p.category === filter);

  return (
    <div className="bg-[#f4f7fa] text-slate-900 antialiased">
      <Navbar />

      <section className="py-16 bg-white border-b border-slate-200" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-[11px] font-bold text-blue-700 tracking-widest uppercase">Catalogue</span>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-950 tracking-tight mt-3 mb-4">
            Products &amp; Prices
          </h1>
          <p className="text-base text-slate-600 max-w-2xl leading-relaxed">
            Popular items from our shelves. Prices shown are indicative — message us on WhatsApp for today's best
            price, stock and delivery.
          </p>
        </div>
      </section>

      <section className="py-12" data-testid="products-catalogue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="category-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                data-testid={`category-filter-${c.key}`}
                onClick={() => setFilter(c.key)}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors border ${
                  filter === c.key
                    ? "bg-blue-700 text-white border-blue-700"
                    : "bg-white text-slate-600 border-slate-300 hover:border-blue-500 hover:text-blue-700"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-sm text-slate-500" data-testid="products-loading">Loading products...</p>
          ) : shown.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="products-empty">
              Products are being updated. Please WhatsApp us for the latest catalogue.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {shown.map((p, i) => {
                const wa = `https://wa.me/919076585965?text=${encodeURIComponent(
                  `Hi SUDHEER ELECTRONIC, I want to enquire about: ${p.name} (listed ${inr.format(p.price)}). Please share today's best price.`
                )}`;
                return (
                  <motion.div
                    key={p.slug}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
                    data-testid={`product-card-${p.slug}`}
                    className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-shadow"
                  >
                    <div className="relative aspect-[4/3]">
                      <img src={CATEGORY_IMG[p.category] || IMAGES.electronics} alt={p.name} loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover" />
                      <span className="absolute top-2 left-2 bg-blue-700 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
                        {CATEGORIES.find((c) => c.key === p.category)?.label || "Electronics"}
                      </span>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-sm font-bold text-slate-950 mb-1 leading-snug">{p.name}</h3>
                      <p className="text-lg font-black text-blue-700" data-testid={`product-price-${p.slug}`}>{inr.format(p.price)}</p>
                      <p className="text-[10px] text-slate-500 mb-4">Indicative price — confirm on WhatsApp</p>
                      <div className="mt-auto flex gap-2">
                        <a href={wa} target="_blank" rel="noopener noreferrer" data-testid={`product-whatsapp-${p.slug}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-bold rounded transition-colors">
                          <MessageCircle size={14} /> WhatsApp
                        </a>
                        <Link to={`/contact?product=${encodeURIComponent(p.name)}`} data-testid={`product-enquire-${p.slug}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-blue-700 hover:bg-blue-600 text-white text-[11px] font-bold rounded transition-colors">
                          <FileText size={14} /> Enquire
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
